// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * Report export by device (vic/vetpo#56).
 *
 * A vet used to pick a report window by DATE (7/14/30/90 presets or custom).
 * Prod (2026-09-26): 7 of 115 exports covered under a day and 2 over 100 days,
 * so hand-picked ranges are often wrong. Picking the sensor(s) instead makes
 * the window exactly the wear period. These are the pure pieces: the list
 * rows, the window a selection implies, and the clamp for the custom fallback.
 */

import { format } from 'date-fns'

const DAY_MS = 24 * 60 * 60 * 1000

/** Devices with this many days of data or fewer get a "<3 days" label. */
export const SHORT_WEAR_MAX_DAYS = 3

export interface DeviceReadingBounds {
  first: Date | null
  last: Date | null
}

export interface DeviceWearRow {
  id: string | number
  name: string
  /** First and last reading — null when the device never reported. */
  first: Date | null
  last: Date | null
  /** Whole days between first and last reading (at least 1 when there is data). */
  days: number
  /** Data span of SHORT_WEAR_MAX_DAYS or less — labelled, never hidden. */
  shortWear: boolean
}

export type ExportSelectionMode = 'single' | 'multi' | 'custom'

export function wearDays(first: Date, last: Date): number {
  return Math.max(1, Math.round((last.getTime() - first.getTime()) / DAY_MS))
}

/**
 * One row per device, newest wear first (by first reading); devices that never
 * reported sort last. Missing names fall back to the id so a row is never blank.
 */
export function buildDeviceWearRows(
  devices: ReadonlyArray<{ id: string | number; name?: string | null }>,
  bounds: ReadonlyMap<string, DeviceReadingBounds>,
): DeviceWearRow[] {
  const rows = devices.map((d): DeviceWearRow => {
    const b = bounds.get(String(d.id))
    const first = b?.first ?? null
    const last = b?.last ?? null
    const hasData = first !== null && last !== null
    const spanMs = hasData ? last.getTime() - first.getTime() : 0
    return {
      id: d.id,
      name: d.name || `#${d.id}`,
      first,
      last,
      days: hasData ? wearDays(first, last) : 0,
      shortWear: hasData && spanMs <= SHORT_WEAR_MAX_DAYS * DAY_MS,
    }
  })
  return rows.sort((a, b) => {
    if (a.first && b.first) return b.first.getTime() - a.first.getTime()
    if (a.first) return -1
    if (b.first) return 1
    return String(a.id).localeCompare(String(b.id))
  })
}

/**
 * The report window a device selection implies: earliest first reading to the
 * latest last reading of the ticked devices. Gaps between non-adjacent devices
 * stay inside the window and render as gaps — nothing is interpolated.
 * Null when nothing ticked has data.
 */
export function selectionWindow(
  rows: ReadonlyArray<DeviceWearRow>,
  selectedIds: ReadonlyArray<string | number>,
): { start: Date; end: Date } | null {
  const wanted = new Set(selectedIds.map(String))
  let start: number | null = null
  let end: number | null = null
  for (const r of rows) {
    if (!wanted.has(String(r.id)) || !r.first || !r.last) continue
    start = start === null ? r.first.getTime() : Math.min(start, r.first.getTime())
    end = end === null ? r.last.getTime() : Math.max(end, r.last.getTime())
  }
  return start === null || end === null ? null : { start: new Date(start), end: new Date(end) }
}

/** Custom ranges may not reach outside the pet's actual data range. */
export function clampRangeToData(
  start: Date,
  end: Date,
  data: { startDate: Date; endDate: Date } | null,
): { start: Date; end: Date } {
  if (!data) return { start, end }
  const lo = data.startDate.getTime()
  const hi = data.endDate.getTime()
  const s = Math.min(Math.max(start.getTime(), lo), hi)
  const e = Math.max(Math.min(end.getTime(), hi), s)
  return { start: new Date(s), end: new Date(e) }
}

export function exportSelectionMode(
  mode: 'devices' | 'custom',
  selectedIds: ReadonlyArray<string | number>,
): ExportSelectionMode {
  if (mode === 'custom') return 'custom'
  return selectedIds.length > 1 ? 'multi' : 'single'
}

/** The rows a selection keeps, in wear order (oldest first) — for the report header. */
export function selectedRowsInWearOrder(
  rows: ReadonlyArray<DeviceWearRow>,
  selectedIds: ReadonlyArray<string | number>,
): DeviceWearRow[] {
  const wanted = new Set(selectedIds.map(String))
  return rows
    .filter((r) => wanted.has(String(r.id)) && r.first && r.last)
    .sort((a, b) => (a.first as Date).getTime() - (b.first as Date).getTime())
}

/**
 * Title of a sensor's trend chart in the PDF report (vic/vetpo#56), e.g.
 * "Sensor B · 8/1–8/9 (9 days)".
 */
export function formatTrendSeriesTitle(
  name: string,
  first: Date,
  last: Date,
  days: number,
): string {
  return `${name} · ${format(first, 'M/d')}–${format(last, 'M/d')} (${days} day${days === 1 ? '' : 's'})`
}
